import { useState, useLayoutEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ProductGroupCard from '@/components/ProductGroupCard';
import BlurImage from '@/components/BlurImage';
import { ProductCardSkeleton } from '@/components/Skeletons';
import { getFeaturedProductGroups, resolveImageUrl } from '@/lib/api';
import { FormattedText } from '@/lib/formatters';
import { useRegion } from '@/context/RegionContext';
import { useCategories, useSiteContent } from '@/hooks/useApiQueries';
import SEO from '@/components/SEO';
import AdPlaceholder from '@/components/AdPlaceholder';

const TikTokIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.627 0-12 5.372-12 12 0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146 1.124.347 2.317.535 3.554.535 6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

const YoutubeIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const SubstackIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/>
  </svg>
);

// Default content (fallback)
const DEFAULT_HOMEPAGE_CONTENT = {
  hero_label: 'Curated Amazon Finds',
  hero_tagline_prefix: 'Curated for the',
  hero_tagline_highlight: 'cool girls',
  hero_subtitle: "A vibe, not a style. Cool-girl isn't a look — it's how you feel.",
  hero_description: "This is where I share my **favorite Amazon finds** — the pieces I actually love, style, and genuinely obsess over. You'll see my *real-life outfits* and how I wear them day to day, mixed with **AI-edits of looks** I'm manifesting into my closet next. Think elevated basics, statement moments, subtle glow-ups, and that effortless cool-girl energy that makes everything feel intentional without feeling forced.",
  hero_image_url: 'https://customer-assets.emergentagent.com/job_4f5c0ea9-425c-4471-ae6d-15b5f34fda60/artifacts/d7ohc1r3_Presentation_image.JPG',
  hero_image_alt: 'Teresa - The Cool Girl Cut Creator',
  creator_name: '@teresarcordeiro',
  creator_label: 'Creator',
  creator_url: '',
  cta_primary_text: 'Shop Now',
  cta_primary_link: '/search',
  social_tiktok_url: 'https://tiktok.com/@the.cool.girl.cut',
  social_tiktok_label: 'TikTok',
  social_instagram_url: 'https://instagram.com/teresarcordeiro',
  social_instagram_label: 'Instagram',
  social_facebook_url: '',
  social_facebook_label: 'Facebook',
  social_pinterest_url: 'https://pinterest.com/thecoolgirlcut',
  social_pinterest_label: 'Pinterest',
  social_youtube_url: 'https://youtube.com/@teresarcordeiro',
  social_youtube_label: 'AI Edits',
  social_substack_url: '',
  social_substack_label: 'Substack',
  featured_section_label: 'Handpicked',
  featured_section_title: "Editor's Picks",
  quick_links_title: 'Quick Links',
  quick_links: []
};

const HomePage = () => {
  const location = useLocation();
  const { region } = useRegion();
  const [featuredGroups, setFeaturedGroups] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  
  // Use React Query for cached data
  const { data: categoriesData = [], isLoading: categoriesLoading } = useCategories();
  const { data: siteContentData, isLoading: contentLoading } = useSiteContent();
  
  // Filter categories for homepage display
  const categories = useMemo(() => 
    categoriesData.filter(c => c.show_on_homepage && c.active !== false),
    [categoriesData]
  );
  
  // Merge site content with defaults
  const content = useMemo(() => ({
    ...DEFAULT_HOMEPAGE_CONTENT,
    ...(siteContentData?.homepage || {})
  }), [siteContentData]);
  
  const loading = featuredLoading || categoriesLoading || contentLoading;

  // Scroll to top when page loads
  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Load featured products (not cached - changes frequently)
  useLayoutEffect(() => {
    const loadFeatured = async () => {
      setFeaturedLoading(true);
      try {
        const featured = await getFeaturedProductGroups(region);
        setFeaturedGroups(featured);
      } catch (error) {
        console.error('Error loading featured products:', error);
        setFeaturedGroups([]);
      } finally {
        setFeaturedLoading(false);
      }
    };
    loadFeatured();
  }, [region]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="animate-fade-in-up">
      <SEO 
        title={content.seo_title || undefined}
        description={content.seo_description || ((content.hero_subtitle && content.hero_subtitle.length > 0) ? content.hero_subtitle : "Curated Amazon finds for the cool girls. Shop fashion, beauty & lifestyle.")}
        keywords={content.seo_keywords}
        image={(content.hero_image && content.hero_image.length > 0) ? content.hero_image : undefined}
        url="/"
        type="website"
        isHomepage={true}
      />
      
      {/* Hero Section - Asymmetric Layout */}
      <section 
        className="hero-section px-10 md:px-20 lg:px-28 xl:px-36 pt-6 md:pt-10 pb-0"
        data-testid="hero-section"
      >
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-10 items-start">
          <div className="space-y-6 min-w-0">
            <div className="space-y-2">
              <p className="font-mono text-xs uppercase tracking-widest text-site-text-muted">
                {content.hero_label}
              </p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.9] italic">
                {content.hero_tagline_prefix} 
                <span className="text-site-accent-blue"> {content.hero_tagline_highlight}</span>
              </h1>
            </div>
            <div className="space-y-4 pt-2">
              <p className="text-base md:text-lg font-semibold text-site-text leading-relaxed" style={{ fontFamily: "'Abel', sans-serif" }}>
                {content.hero_subtitle}
              </p>
              <p className="text-sm md:text-base text-site-text-muted leading-loose [&_strong]:font-semibold [&_strong]:text-site-text [&_em]:italic" style={{ fontFamily: "'Abel', sans-serif" }}>
                <FormattedText>{content.hero_description}</FormattedText>
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-2 max-w-full xl:max-w-none">
              {content.cta_primary_link && (
                <Link 
                  to={content.cta_primary_link} 
                  className="btn-primary inline-flex items-center justify-center gap-2 text-[10px] py-2.5 whitespace-nowrap w-[115px]"
                  data-testid="shop-now-btn"
                >
                  {content.cta_primary_text || 'Shop Now'}
                  <ArrowRight className="w-4 h-4 flex-shrink-0" />
                </Link>
              )}
              {content.social_tiktok_url && (
                <a 
                  href={content.social_tiktok_url}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-[10px] py-2.5 whitespace-nowrap w-[115px]"
                  data-testid="follow-tiktok-btn"
                >
                  <span className="flex-shrink-0"><TikTokIcon /></span>
                  {content.social_tiktok_label || 'TikTok'}
                </a>
              )}
              {content.social_instagram_url && (
                <a 
                  href={content.social_instagram_url}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-[10px] py-2.5 whitespace-nowrap w-[115px]"
                  data-testid="follow-instagram-btn"
                >
                  <span className="flex-shrink-0"><InstagramIcon /></span>
                  {content.social_instagram_label || 'Instagram'}
                </a>
              )}
              {content.social_facebook_url && (
                <a 
                  href={content.social_facebook_url}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-[10px] py-2.5 whitespace-nowrap w-[115px]"
                  data-testid="follow-facebook-btn"
                >
                  <span className="flex-shrink-0"><FacebookIcon /></span>
                  {content.social_facebook_label || 'Facebook'}
                </a>
              )}
              {content.social_pinterest_url && (
                <a 
                  href={content.social_pinterest_url}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-[10px] py-2.5 whitespace-nowrap w-[115px]"
                  data-testid="follow-pinterest-btn"
                >
                  <span className="flex-shrink-0"><PinterestIcon /></span>
                  {content.social_pinterest_label || 'Pinterest'}
                </a>
              )}
              {content.social_youtube_url && (
                <a 
                  href={content.social_youtube_url}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-[10px] py-2.5 whitespace-nowrap w-[115px]"
                  data-testid="follow-youtube-btn"
                >
                  <span className="flex-shrink-0"><YoutubeIcon /></span>
                  {content.social_youtube_label || 'AI Edits'}
                </a>
              )}
              {content.social_substack_url && (
                <a 
                  href={content.social_substack_url}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-[10px] py-2.5 whitespace-nowrap w-[115px]"
                  data-testid="follow-substack-btn"
                >
                  <span className="flex-shrink-0"><SubstackIcon /></span>
                  {content.social_substack_label || 'Substack'}
                </a>
              )}
            </div>
          </div>
          <div className="relative flex justify-center lg:justify-end flex-shrink-0">
            <div className="relative inline-block">
              <BlurImage
                src={resolveImageUrl(content.hero_image_url)}
                alt={content.hero_image_alt}
                className="h-auto max-h-[400px] w-auto max-w-[280px] lg:max-w-[320px] object-contain"
                containerClassName="inline-block"
                priority={true}
              />
              {/* Floating badge - bottom right corner of image */}
              {content.creator_url ? (
                <a 
                  href={content.creator_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm border border-black/10 p-2 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 cursor-pointer"
                  data-testid="creator-badge-link"
                >
                  <p className="font-mono text-[9px] uppercase tracking-widest text-site-text-muted">{content.creator_label}</p>
                  <p className="font-heading text-sm italic">{content.creator_name}</p>
                </a>
              ) : (
                <div 
                  className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm border border-black/10 p-2 shadow-lg"
                  data-testid="creator-badge"
                >
                  <p className="font-mono text-[9px] uppercase tracking-widest text-site-text-muted">{content.creator_label}</p>
                  <p className="font-heading text-sm italic">{content.creator_name}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section - Responsive Grid */}
      <section className="mt-2 sm:mt-3 md:mt-2 lg:mt-1 xl:mt-0" data-testid="categories-section">

        {/* Ad Banner - Below Hero */}
        <div className="px-6 md:px-12 lg:px-24 py-4 flex justify-center">
          <AdPlaceholder slot="leaderboard" className="max-w-[728px]" />
        </div>
        {(() => {
          // Limit to max 6 categories
          const displayCategories = categories.slice(0, 6);
          const count = displayCategories.length;
          
          // Determine grid layout based on count
          // 1-4 categories: single row
          // 5-6 categories: 3 in first row, rest in second row
          const isSingleRow = count <= 4;
          
          return (
            <div 
              className={`grid border-t border-site-border-strong ${
                isSingleRow 
                  ? `grid-cols-1 md:grid-cols-${count}` 
                  : 'grid-cols-1 md:grid-cols-3'
              }`}
              style={isSingleRow && count > 1 ? { 
                gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` 
              } : undefined}
            >
              {displayCategories.map((category, index) => {
                // Calculate border logic
                let hasBorderRight = false;
                if (isSingleRow) {
                  // Single row: all except last have right border
                  hasBorderRight = index < count - 1;
                } else {
                  // Two rows of 3: items 0,1 and 3,4 have right borders
                  hasBorderRight = (index % 3 !== 2) && (index < count - 1 || count === 6);
                }
                
                // Bottom border for first row when we have 2 rows
                const hasBorderBottom = !isSingleRow && index < 3;
                
                // First 3 categories are above the fold, prioritize loading
                const isPriority = index < 3;
                
                return (
                  <Link
                    key={category.id}
                    to={`/${category.slug}`}
                    className={`category-card aspect-[4/3] relative group overflow-hidden ${
                      hasBorderRight ? 'border-r border-site-text' : ''
                    } ${
                      hasBorderBottom ? 'border-b border-site-text' : ''
                    }`}
                    data-testid={`category-${category.id}`}
                  >
                    <BlurImage
                      src={resolveImageUrl(category.homepage_image) || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600'}
                      alt={category.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      containerClassName="w-full h-full"
                      priority={isPriority}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
                    <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 z-10 text-white">
                      <p className="font-mono text-[8px] md:text-[10px] uppercase tracking-widest mb-1 md:mb-2 text-white/70">
                        {category.shop_by_label || 'Shop by style'}
                      </p>
                      <h2 className="font-heading text-xl md:text-2xl lg:text-3xl italic leading-tight">
                        {category.title}
                      </h2>
                      <span className="hidden md:inline-flex items-center gap-2 mt-3 font-mono text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                        Explore <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          );
        })()}
      </section>

      {/* Featured Products - Show section with spacing even if empty */}
      {!featuredLoading && (
        <section className="px-6 md:px-12 lg:px-24 pt-10 md:pt-14 pb-8" data-testid="featured-section">
          {featuredGroups.length > 0 ? (
            <>
              <div className="flex items-end justify-between mb-12">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted mb-2">{content.featured_section_label}</p>
                  <h2 className="font-heading text-4xl md:text-5xl italic tracking-tight">
                    {content.featured_section_title}
                  </h2>
                </div>
                <Link 
                  to="/editors-picks" 
                  className="font-mono text-xs uppercase tracking-widest hover:text-site-accent-blue inline-flex items-center gap-2 transition-colors"
                  data-testid="view-all-featured"
                >
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              
              {/* Featured Products with Sidebar Ad */}
              <div className="flex gap-6">
                <div className="flex-1">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {featuredGroups.slice(0, 4).map((group) => (
                      <ProductGroupCard key={group.id} group={group} />
                    ))}
                  </div>
                </div>
                
                {/* Sidebar Ad - Desktop Only */}
                <div className="hidden xl:block flex-shrink-0">
                  <AdPlaceholder slot="sidebar" className="sticky top-4" />
                </div>
              </div>
              
              {/* In-Feed Ad - Mobile/Tablet */}
              <div className="xl:hidden mt-6">
                <AdPlaceholder slot="in-feed" />
              </div>
            </>
          ) : (
            /* Empty spacer when no featured products for this region */
            <div className="h-0" data-testid="featured-section-empty" />
          )}
        </section>
      )}

      {/* Quick Links Section - Only show if links exist */}
      {content.quick_links && content.quick_links.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-3 md:py-4 bg-site-bg-alt" data-testid="quick-links-section">
          <div className="flex flex-wrap justify-center gap-4">
            {content.quick_links.map((link, index) => {
              if (!link.title || !link.url) return null;
              const isExternal = link.url.startsWith('http');
              const linkClasses = "px-6 py-3 border border-site-text font-mono text-xs uppercase tracking-widest hover:bg-site-bg-dark hover:text-white transition-colors";
              
              return isExternal ? (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClasses}
                  data-testid={`quick-link-${index}`}
                >
                  {link.title}
                </a>
              ) : (
                <Link
                  key={index}
                  to={link.url}
                  className={linkClasses}
                  data-testid={`quick-link-${index}`}
                >
                  {link.title}
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

// Loading Skeleton for initial page load
const LoadingSkeleton = () => (
  <div className="animate-fade-in-up">
    {/* Hero Section Skeleton */}
    <section className="px-10 md:px-20 lg:px-28 xl:px-36 pt-6 md:pt-10 pb-0">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-10 items-start">
        <div className="space-y-6 min-w-0">
          <div className="space-y-2">
            <div className="h-3 w-32 bg-site-bg-alt rounded animate-pulse" />
            <div className="h-12 w-3/4 bg-site-bg-alt rounded animate-pulse" />
          </div>
          <div className="space-y-4 pt-2">
            <div className="h-5 w-full bg-site-bg-alt rounded animate-pulse" />
            <div className="h-4 w-2/3 bg-site-bg-alt rounded animate-pulse" />
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 w-28 bg-site-bg-alt rounded animate-pulse" />
            ))}
          </div>
        </div>
        <div className="relative flex justify-center lg:justify-end flex-shrink-0">
          <div className="h-[400px] w-[280px] lg:w-[320px] bg-site-bg-alt rounded animate-pulse" />
        </div>
      </div>
    </section>
    
    {/* Categories Skeleton */}
    <section className="mt-14 sm:mt-16 md:mt-14 lg:mt-12 xl:mt-10 2xl:mt-8">
      <div className="grid grid-cols-1 md:grid-cols-3 border-t border-site-border-strong">
        {[1, 2, 3].map((i) => (
          <div key={i} className="aspect-[4/3] bg-site-bg-alt border-r border-site-border-strong animate-pulse" />
        ))}
      </div>
    </section>
    
    {/* Featured Products Skeleton */}
    <section className="px-6 md:px-12 lg:px-24 pt-10 md:pt-14 pb-8">
      <div className="flex items-end justify-between mb-12">
        <div>
          <div className="h-3 w-24 bg-site-bg-alt rounded mb-2 animate-pulse" />
          <div className="h-10 w-48 bg-site-bg-alt rounded animate-pulse" />
        </div>
        <div className="h-4 w-20 bg-site-bg-alt rounded animate-pulse" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>
    </section>
  </div>
);

export default HomePage;
